import React, { useState } from "react";
import cn from "classnames";
import styles from "./Faq.module.sass";
import Item from "./Item";
import Dropdown from "../../../components/Dropdown";

const items = [
  {
    title: "Général",
    items: [
      {
        title: "À qui s'adresse cette solution ?",
        content: "Notre plateforme s'adresse aux CFA, écoles, entreprises accueillant des alternants, et aux organismes de formation souhaitant simplifier leur gestion administrative."
      },
      {
        title: "Est-il possible d'essayer la plateforme gratuitement ?",
        content: "Oui, une période d'essai gratuite est disponible pour découvrir l'ensemble des fonctionnalités sans engagement."
      },
      {
        title: "Est-ce sécurisé ?",
        content: "Oui, toutes les données sont sécurisées, cryptées, et hébergées en France, conformément au RGPD."
      },
      {
        title: "Un étudiant ou une entreprise peut-il remplir le dossier seul ?",
        content: "Oui, chaque partie dispose de son espace personnel sécurisé où elle peut renseigner les informations nécessaires. La complétion est guidée et intuitive."
      },
    ],
  },
  {
    title: "Contrats",
    items: [
      {
        title: "Quels types de contrats sont pris en charge ?",
        content: "Nous prenons en charge les contrats d'apprentissage et les contrats de professionnalisation, incluant la gestion complète des documents officiels (CERFA, conventions, avenants…)."
      },
      {
        title: "La signature électronique est-elle légale ?",
        content: "Oui, nous utilisons une solution de signature électronique certifiée conforme aux normes européennes (eIDAS) avec horodatage et traçabilité complète."
      },
      {
        title: "Comment fonctionne la génération automatique des contrats d'apprentissage ?",
        content: "La plateforme génère automatiquement les contrats CERFA à partir des informations saisies par les différentes parties (étudiant, entreprise, centre de formation). Les champs obligatoires sont pré-remplis et vérifiés pour éviter toute erreur ou rejet du dossier.",
        image: "/images/content/faq-pic.png",
        hasPlayButton: true
      },
      {
        title: "Peut-on personnaliser les modèles de contrat ?",
        content: "Oui, les modèles peuvent être personnalisés selon les besoins de l'établissement : clauses spécifiques, conventions pédagogiques, annexes, etc. Le système garde une trame conforme à la réglementation tout en offrant une flexibilité."
      },
      {
        title: "Quels types de documents sont générés en plus du CERFA ?",
        content: "Outre le CERFA, la plateforme permet de générer : Des conventions de formation, Des plannings d'alternance, Des avenants, Des attestations et documents de suivi pour l'OPCO ou l'administration."
      },
      {
        title: "Comment suivre l'état d'avancement d'un contrat ?",
        content: "Chaque contrat dispose d'un tableau de bord indiquant son état : en cours de saisie, signé, en attente OPCO, validé, ou rejeté. Des alertes vous informent en temps réel des changements de statut."
      },
      {
        title: "Et pour les avenants ou modifications de contrat ?",
        content: "Les avenants sont gérés directement depuis le dossier initial. En quelques clics, vous pouvez éditer, faire signer et envoyer l'avenant à l'OPCO concerné."
      },
    ],
  },
  {
    title: "Opco",
    items: [
      {
        title: "Est-ce que la plateforme envoie les dossiers aux OPCO ?",
        content: "Oui, une fois le dossier complété et signé, il est envoyé automatiquement à l'OPCO concerné via une connexion directe."
      },
      {
        title: "Puis-je suivre les échéances de paiements ?",
        content: "Absolument. La plateforme intègre un suivi complet des échéanciers avec notifications et historique des paiements effectués."
      },
      {
        title: "Comment sont envoyés les dossiers aux OPCO ?",
        content: "Une fois les documents validés et signés, le dossier est envoyé directement via une interface connectée aux OPCO. Cette transmission est sécurisée, traçable, et conforme aux standards des opérateurs de compétences."
      },
      {
        title: "Quels OPCO sont compatibles avec la plateforme ?",
        content: "La solution est interconnectée avec tous les OPCO français. Une mise à jour régulière est effectuée pour suivre les évolutions de chaque OPCO et assurer la bonne transmission des dossiers."
      },
      {
        title: "Que se passe-t-il en cas de rejet par l'OPCO ?",
        content: "Si un dossier est rejeté, vous êtes immédiatement notifié. Un diagnostic automatique vous indique les erreurs ou documents manquants pour corriger rapidement et renvoyer le dossier."
      },
      {
        title: "Est-ce que les délais de traitement OPCO sont réduits ?",
        content: "Oui. Grâce à la complétude des dossiers, à la conformité des documents et à l'envoi automatique, les délais de traitement par les OPCO sont souvent plus courts, ce qui accélère la prise en charge financière."
      },
      {
        title: "Peut-on envoyer plusieurs dossiers en même temps ?",
        content: "Oui, la plateforme permet l'envoi groupé de dossiers à l'OPCO. C'est idéal pour les rentrées ou les campagnes de signature massives."
      },
    ],
  },
];

const Faq = () => {
  const options = [];
  items.map((x) => options.push(x.title));

  const [category, setCategory] = useState(options[0]);

  return (
    <div className={cn("section", styles.section)}>
      <div className={cn("container", styles.container)}>
        <div className={styles.top}>
          <div className={cn("stage-small", styles.stage)}>
            Questions fréquentes
          </div>
          <h2 className={cn("h2", styles.title)}>FAQ – Questions fréquentes</h2>
          <div className={styles.info}>
            Découvrez les réponses aux questions les plus courantes sur notre plateforme de gestion des contrats d'alternance ou <a href="/contact">Contactez notre support</a>
          </div>
          <div className={styles.nav}>
            {items.map((x, index) => (
              <button
                className={cn(styles.btn, {
                  [styles.active]: x.title === category,
                })}
                onClick={() => setCategory(x.title)}
                key={index}
              >
                {x.title}
              </button>
            ))}
          </div>
          <Dropdown
            className={styles.dropdown}
            value={category}
            setValue={setCategory}
            options={options}
          />
        </div>
        <div className={styles.list}>
          {items
            .find((x) => x.title === category)
            .items.map((x, index) => (
              <Item item={x} key={index} />
            ))}
        </div>
      </div>
    </div>
  );
};

export default Faq;
